import {
  Box,
  Button,
  Card,
  Grid,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

interface ProductDetailsTabsProps {
  activeTab: number;
  product: {
    title: string;
    categories?: string[];
    productDetails: Array<{ name: string; value: unknown }>;
    features: string[];
    reviewCount: number;
    modelNumber: string;
    upc: string;
  };
  onTabChange: (event: React.SyntheticEvent, newValue: number) => void;
}

const ProductDetailsTabs: React.FC<ProductDetailsTabsProps> = ({
  activeTab,
  product,
  onTabChange,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [showAllSpecs, setShowAllSpecs] = useState(false);
  const [showMoreFeatures, setShowMoreFeatures] = useState(false);
  const [expandedSpecs, setExpandedSpecs] = useState<Record<number, boolean>>(
    {}
  );
  const toggleSpecExpansion = (index: number) => {
    setExpandedSpecs((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  return (
    <Card
      sx={{
        mb: 4,
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        boxShadow: "0 12px 28px rgba(15, 23, 42, 0.06)",
        overflow: "hidden",
      }}
    >
      <Tabs
        value={activeTab}
        onChange={onTabChange}
        variant={isMobile ? "fullWidth" : "standard"}
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          backgroundColor: "#fafafa",
          "& .MuiTab-root": {
            fontWeight: 700,
            textTransform: "none",
            minHeight: 48,
          },
        }}
        TabIndicatorProps={{ sx: { height: 3 } }}
      >
        <Tab
          label={t("products.productSpecifications")}
          className="notranslate"
          translate="no"
        />
        {product.features.length > 0 && (
          <Tab
            label={t("products.aboutThisItem")}
            className="notranslate"
            translate="no"
          />
        )}
      </Tabs>

      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        {activeTab === 0 && (
          <Grid container spacing={2}>
            {product.productDetails
              .slice(0, showAllSpecs ? product.productDetails.length : 10)
              .map((detail, index: number) => (
                <Grid item xs={12} sm={6} key={index}>
                  {(() => {
                    const formattedName = detail.name
                      .replace(/_/g, " ")
                      .replace(/\b\w/g, (l: string) => l.toUpperCase());
                    const isLong = formattedName.length > 25;
                    const displayName = isLong
                      ? formattedName.substring(0, 22) + "..."
                      : formattedName;

                    return (
                      <Tooltip title={formattedName} disableHoverListener={!isLong}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: "bold",
                            display: "inline-block",
                            cursor: isLong ? "help" : "default",
                          }}
                        >
                          {displayName}:
                        </Typography>
                      </Tooltip>
                    );
                  })()}
                  {(() => {
                    const rawValue = detail.value;
                    const fallbackValue =
                      rawValue == null ? "" : String(rawValue);
                    const displayValue =
                      rawValue == null ? "" : String(rawValue);
                    const isExpandable =
                      typeof displayValue === "string" &&
                      displayValue.length > 160;
                    const isExpanded = !!expandedSpecs[index];

                    if (typeof displayValue === "string") {
                      if (isExpandable) {
                        return (
                          <Box>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={
                                isExpanded
                                  ? {}
                                  : {
                                    display: "-webkit-box",
                                    WebkitLineClamp: 3,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                  }
                              }
                            >
                              {displayValue}
                            </Typography>
                            <Typography
                              variant="body2"
                              onClick={() => toggleSpecExpansion(index)}
                              sx={{
                                color: "primary.main",
                                cursor: "pointer",
                                mt: 0.5,
                                fontSize: "0.875rem",
                                "&:hover": {
                                  textDecoration: "underline",
                                },
                              }}
                              className="notranslate"
                              translate="no"
                            >
                              {isExpanded
                                ? t("common.showLess")
                                : t("common.showMore")}
                            </Typography>
                          </Box>
                        );
                      }

                      return (
                        <Typography variant="body2" color="text.secondary">
                          {displayValue}
                        </Typography>
                      );
                    }

                    return (
                      <Typography variant="body2" color="text.secondary">
                        {fallbackValue}
                      </Typography>
                    );
                  })()}
                </Grid>
              ))}
            <Grid item xs={12} sm={6}>
              <Typography
                variant="body2"
                sx={{ fontWeight: "bold" }}
                className="notranslate"
                translate="no"
              >
                {t("products.modelNumber")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {product.modelNumber}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography
                variant="body2"
                sx={{ fontWeight: "bold" }}
                className="notranslate"
                translate="no"
              >
                {t("products.upc")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {product.upc}
              </Typography>
            </Grid>
            {product.productDetails.length > 10 && (
              <Grid item xs={12}>
                <Typography
                  variant="body2"
                  onClick={() => setShowAllSpecs(!showAllSpecs)}
                  sx={{
                    color: "primary.main",
                    cursor: "pointer",
                    mt: 1,
                    fontSize: "0.875rem",
                    "&:hover": { textDecoration: "underline" },
                  }}
                  className="notranslate"
                  translate="no"
                >
                  {showAllSpecs
                    ? `▲ ${t("common.showLess")}`
                    : `▼ ${t("common.showMore")}`}
                </Typography>
              </Grid>
            )}
          </Grid>
        )}

        {activeTab === 1 && (
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {(showMoreFeatures
              ? product.features
              : product.features.slice(0, 3)
            ).map((feature: string, index: number) => (
              <Box component="li" key={index}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {feature}
                </Typography>
              </Box>
            ))}
            {product.features.length > 3 && (
              <Button
                onClick={() => setShowMoreFeatures((prev) => !prev)}
                sx={{
                  color: "primary.main",
                  cursor: "pointer",
                  mt: 1,
                  fontSize: "0.875rem",
                  "&:hover": { textDecoration: "underline" },
                }}
                className="notranslate"
                translate="no"
              >
                {showMoreFeatures
                  ? `▲ ${t("common.showLess")}`
                  : `▼ ${t("common.showMore")}`}
              </Button>
            )}
          </Box>
        )}


      </Box>
    </Card>
  );
};

export default ProductDetailsTabs;
